import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeMode } from '../context/ThemeContext';

const ThemeToggle = ({ size = 'medium', showLabel = false }) => {
  const { mode, toggleTheme, isDark } = useThemeMode();
  const handleThemeToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleTheme();
  };

  return (
    <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          type="button"
          onClick={handleThemeToggle}
          size={size}
          aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: isDark 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.05)',
            border: '1px solid',
            borderColor: isDark 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.08)',
            color: isDark ? '#fbbf24' : '#6366f1',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            '&:hover': {
              backgroundColor: isDark 
                ? 'rgba(255, 255, 255, 0.15)' 
                : 'rgba(0, 0, 0, 0.08)',
              transform: 'scale(1.05)',
              boxShadow: isDark 
                ? '0 0 20px rgba(251, 191, 36, 0.3)' 
                : '0 0 20px rgba(99, 102, 241, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={mode}
              initial={{ y: -30, opacity: 0, rotate: -90 }}
              animate={{ y: 0, opacity: 1, rotate: 0 }}
              exit={{ y: 30, opacity: 0, rotate: 90 }}
              transition={{ 
                duration: 0.3, 
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isDark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
            </motion.div>
          </AnimatePresence>
        </IconButton>
        
        {showLabel && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'inherit',
            }}
          >
            {isDark ? 'Light' : 'Dark'}
          </motion.span>
        )}
      </Box>
    </Tooltip>
  );
};

// Minimal version for tight spaces
export const ThemeToggleMinimal = () => {
  const { toggleTheme, isDark } = useThemeMode();

  return (
    <IconButton
      type="button"
      onClick={(event) => { event.preventDefault(); event.stopPropagation(); toggleTheme(); }}
      size="small"
      sx={{
        color: isDark ? '#fbbf24' : '#6366f1',
        '&:hover': {
          backgroundColor: isDark 
            ? 'rgba(251, 191, 36, 0.1)' 
            : 'rgba(99, 102, 241, 0.1)',
        },
      }}
    >
      {isDark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
    </IconButton>
  );
};

// Switch style toggle
export const ThemeSwitch = () => {
  const { toggleTheme, isDark } = useThemeMode();

  return (
    <Box
      component="button"
      type="button"
      onClick={(event) => { event.preventDefault(); event.stopPropagation(); toggleTheme(); }}
      sx={{
        width: 56,
        height: 28,
        borderRadius: '14px',
        border: 0,
        padding: '2px',
        font: 'inherit',
        backgroundColor: isDark ? '#4f46e5' : '#e2e8f0',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        padding: '2px',
        '&:hover': {
          boxShadow: isDark 
            ? '0 0 12px rgba(79, 70, 229, 0.5)' 
            : '0 0 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <motion.div
        animate={{
          x: isDark ? 28 : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
        style={{
          width: 24,
          height: 24,
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            color: isDark ? '#fbbf24' : '#6366f1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'scale(0.7)',
          }}
        >
          {isDark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
        </Box>
      </motion.div>
    </Box>
  );
};

export default ThemeToggle;
